import React, { useState } from 'react';

/**
 * Client confirmation badge for the claim header (Barclays sole-representative
 * email, Dec 4 Nov 2026).
 *
 * Green  "✓ Representation confirmed"     the client pressed Confirm on their
 *                                          email link (claim.clientConfirmations).
 * Amber  "Representation confirmation requested"
 *                                          the confirmation email went out
 *                                          (a correspondence entry with
 *                                          metadata.event 'confirmation-email-sent')
 *                                          but the client hasn't confirmed yet.
 *
 * Click a badge to see the detail: when, the exact statement, the address the
 * link went to, IP address and device. Nothing here changes the claim.
 */

export interface ClientConfirmationStamp {
  label?: string;
  confirmedAt?: string;
  tokenId?: string;
  ip?: string;
  userAgent?: string;
  statement?: string;
  emailedTo?: string;
}

export interface ConfirmationBadgeInfo {
  campaign: string;
  label: string;
  state: 'confirmed' | 'awaiting';
  confirmed?: ClientConfirmationStamp;
  sentAt?: string;
  sentTo?: string;
}

const CAMPAIGN_LABELS: Record<string, string> = {
  'barclays-sole-rep-2026-11': 'Barclays',
};

const labelFor = (campaign: string, stamp?: ClientConfirmationStamp): string =>
  stamp?.label || CAMPAIGN_LABELS[campaign] || campaign;

/** "04/11/2026 4:07pm", UK time. */
export const formatUkDateTime = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = date.toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
  const time = date
    .toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(/\s/g, '')
    .toLowerCase();
  return `${day} ${time}`;
};

const asRecord = (value: unknown): Record<string, any> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : null;

/** One badge per campaign: confirmed wins over awaiting. */
export function confirmationBadges(
  clientConfirmations: unknown,
  correspondence: readonly unknown[] | null | undefined,
): ConfirmationBadgeInfo[] {
  const badges = new Map<string, ConfirmationBadgeInfo>();
  const confirmations = asRecord(clientConfirmations) || {};
  Object.entries(confirmations).forEach(([campaign, raw]) => {
    const stamp = asRecord(raw) as ClientConfirmationStamp | null;
    if (!stamp?.confirmedAt) return;
    badges.set(campaign, { campaign, label: labelFor(campaign, stamp), state: 'confirmed', confirmed: stamp });
  });
  (correspondence || []).forEach((raw) => {
    const entry = asRecord(raw);
    const meta = asRecord(entry?.metadata);
    if (!entry || !meta || meta.event !== 'confirmation-email-sent' || !meta.campaign) return;
    const campaign = String(meta.campaign);
    const existing = badges.get(campaign);
    if (existing?.state === 'confirmed') return;
    const sentAt = String(entry.timestamp || '');
    if (existing && (existing.sentAt || '') >= sentAt) return; // keep the latest send
    const to = Array.isArray(entry.to) ? entry.to.join(', ') : String(entry.to || '');
    badges.set(campaign, { campaign, label: labelFor(campaign), state: 'awaiting', sentAt, sentTo: to });
  });
  return Array.from(badges.values());
}

const pill = (confirmed: boolean): React.CSSProperties => ({
  backgroundColor: confirmed ? '#dcfce7' : '#fef3c7',
  color: confirmed ? '#166534' : '#92400e',
  border: `1px solid ${confirmed ? '#86efac' : '#fcd34d'}`,
  padding: '2px 8px',
  borderRadius: '9999px',
  fontSize: '12px',
  fontWeight: 600,
  marginLeft: '4px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  lineHeight: 1.6,
});

const panel: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  zIndex: 50,
  width: '380px',
  maxWidth: '80vw',
  background: '#ffffff',
  color: '#111827',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
  padding: '12px 14px',
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: 1.5,
  whiteSpace: 'normal',
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px', padding: '3px 0' }}>
    <span style={{ color: '#6b7280' }}>{label}</span>
    <span style={{ wordBreak: 'break-word' }}>{children}</span>
  </div>
);

const Badge: React.FC<{ info: ConfirmationBadgeInfo }> = ({ info }) => {
  const [open, setOpen] = useState(false);
  const confirmed = info.state === 'confirmed';
  const stamp = info.confirmed;
  // Dec 4 Nov: "it should say Representation confirmed" (not "Barclays
  // confirmed", which reads as if the lender confirmed). The lender is named
  // in the pop-up and the tooltip.
  const text = confirmed ? '✓ Representation confirmed' : 'Representation confirmation requested';
  const tooltip = confirmed
    ? `${info.label}: client confirmed Complex Law as sole representative on ${formatUkDateTime(stamp?.confirmedAt)}`
    : `${info.label}: confirmation email sent ${formatUkDateTime(info.sentAt)}; the client hasn't confirmed yet`;
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        style={pill(confirmed)}
        title={tooltip}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {text}
      </button>
      {open && (
        <div role="dialog" aria-label={`${info.label} confirmation details`} style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <strong>{`${text.replace(/^✓ /, '')} (${info.label})`}</strong>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '16px', color: '#6b7280' }}
            >
              ×
            </button>
          </div>
          {confirmed ? (
            <>
              <Row label="Confirmed">{formatUkDateTime(stamp?.confirmedAt)}</Row>
              <Row label="Statement">“{stamp?.statement}”</Row>
              <Row label="Link sent to">{stamp?.emailedTo || 'unknown'}</Row>
              <Row label="IP address">{stamp?.ip || 'unknown'}</Row>
              <Row label="Device">{stamp?.userAgent || 'unknown'}</Row>
            </>
          ) : (
            <>
              <Row label="Email sent">{formatUkDateTime(info.sentAt)}</Row>
              <Row label="Sent to">{info.sentTo || 'unknown'}</Row>
              <Row label="Status">Not confirmed yet. The link lasts 90 days.</Row>
            </>
          )}
        </div>
      )}
    </span>
  );
};

interface ClientConfirmationBadgeProps {
  clientConfirmations?: unknown;
  correspondence?: readonly unknown[] | null;
}

const ClientConfirmationBadge: React.FC<ClientConfirmationBadgeProps> = ({ clientConfirmations, correspondence }) => {
  const badges = confirmationBadges(clientConfirmations, correspondence);
  if (badges.length === 0) return null;
  return (
    <>
      {badges.map((info) => (
        <Badge key={info.campaign} info={info} />
      ))}
    </>
  );
};

export default ClientConfirmationBadge;
